import { useEffect } from 'react';
import { AppState, Platform } from 'react-native';
import FingerprintScanner from 'react-native-fingerprint-scanner';

import _ from 'lodash';
import PropTypes from 'prop-types';

const requiresLegacyAuthentication = () => {
  return Platform.OS === 'android' && Platform.Version < 23;
};

const authenticate = ({ onBiometricsSuccess, onBiometricsError, ...config }) => {
  FingerprintScanner.authenticate(config)
    .then(() => {
      onBiometricsSuccess();
    })
    .catch(error => {
      onBiometricsError(error);
    })
    .finally(() => {
      releaseFingerPrintScanner();
    });
};

const releaseFingerPrintScanner = () => {
  if (Platform.OS === 'android') {
    FingerprintScanner.release();
  }
};

const detectFingerprintAvailable = (onSuccess, onError) => {
  return FingerprintScanner.isSensorAvailable()
    .then(biometryType => {
      onSuccess(biometryType);
    })
    .catch(error => onError(error));
};

const BiometricPopup = ({
  showPopup,
  title,
  subTitle,
  description,
  cancelButton,
  fallbackEnabled,
  onBiometricsSuccess,
  onBiometricsError,
  onBiometryTypeChange,
}) => {
  useEffect(() => {
    const handleAppStateChange = nextAppState => {
      if (nextAppState === 'active') {
        releaseFingerPrintScanner();
        detectFingerprintAvailable(onBiometryTypeChange, onBiometricsError);
      }
    };

    detectFingerprintAvailable(onBiometryTypeChange, onBiometricsError);
    const changeListener = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      if (_.isFunction(changeListener?.remove)) {
        changeListener.remove();
      } else {
        // RNSUPPORT: keeps RN 0.64.2 supported
        AppState.removeEventListener('change', handleAppStateChange);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showPopup) {
      const commonProps = {
        description,
        onBiometricsSuccess,
        onBiometricsError,
      };

      if (Platform.OS === 'ios') {
        authenticate({ fallbackEnabled, ...commonProps });
      } else {
        authenticate({ title, subTitle, cancelButton, ...commonProps });
      }
    } else {
      releaseFingerPrintScanner();
    }
  }, [showPopup]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

BiometricPopup.defaultProps = {
  showPopup: false,
  fallbackEnabled: true,
  onBiometricsSuccess: () => {},
  onBiometricsError: () => {},
  onBiometryTypeChange: () => {},
};

BiometricPopup.propTypes = {
  showPopup: PropTypes.bool,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  description: PropTypes.string,
  cancelButton: PropTypes.string,
  fallbackEnabled: PropTypes.bool,
  onBiometricsSuccess: PropTypes.func.isRequired,
  onBiometricsError: PropTypes.func.isRequired,
  onBiometryTypeChange: PropTypes.func.isRequired,
};

export {
  BiometricPopup,
  authenticate,
  requiresLegacyAuthentication,
  releaseFingerPrintScanner,
  detectFingerprintAvailable,
};
